import { Box, Button, Dialog, IconButton, Popover, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { convertToCDNLink, Icons, formatDate, extractAssignmentFileDetailsFromMinioLocationOrKey } from '../../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../../service/api'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import TableHeader from '../../common/TableHeader'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import { DrawerDocumentViewer } from '../../common/DocumentViewer'

const columns = [
    { name: "Student", padding: "10px 20px 10px 40px" },
    { name: "AUID", padding: "10px 20px" },
    { name: "Submitted on", padding: "10px 20px" },
    { name: "Marks Assigned", padding: "10px 20px" },
    { name: "Submitted file", align: "right", padding: "10px 40px 10px 20px" },
]

const SubmissionsViewer = ( { selectedValue, assignmentId, open, handleClose } ) => {

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [previewFile, setPreviewFile] = useState( null )
    const [submissions, setSubmissions] = useState( [] )
    const [submissionFiles, setSubmissionFiles] = useState( [] )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )
    const [selectedSubmissionAnchor, setSelectedSubmissionAnchor] = useState( null )

    const { palette, table, border } = useTheme()

    const viewSubmission = async ( value ) => {
        try {
            const { data: { data } } = await api.downloadAssignment( { sourceUrl: decodeURIComponent( value ), preSignedUrl: true }, true )
            const { groups: fileDetails } = extractAssignmentFileDetailsFromMinioLocationOrKey( value )

            setPreviewingFileDetails( fileDetails )
            setPreviewFile( convertToCDNLink( data ) )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || `An error occurred while viewing the submission!` )
        }
    }

    const getData = useCallback( async () => {
        try {
            const { data: { data } } = await api.fetchAssignmentSubmission( assignmentId )
            setSubmissions( data )
            setError( null )
        } catch ( err ) {
            setError( { code: err?.response?.status || 500, err: err?.response?.data?.message || "Something went wrong while fetching submissions." } )
        } finally {
            setLoading( false )
        }
    }, [assignmentId] )

    useEffect( () => {
        getData()
    }, [getData] )


    return (
        <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={open} slotProps={{
            paper: { sx: { width: "90%", maxWidth: "1024px" } }
        }}>
            <Popover sx={{ bgcolor: "rgba(48,48,48,0.6)" }} slotProps={{ paper: { sx: { overflow: "auto" } } }} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "right" }} onClose={() => setSubmissionFiles( [] )} anchorEl={selectedSubmissionAnchor} open={submissionFiles?.length > 0}>
                <Typography variant='subtitle2' borderBottom={border[1]} padding="10px">Submissions</Typography>
                <Box maxHeight="380px" overflow="auto" className="custom-scrollbar">
                    {submissionFiles.map( item => {
                        const { groups: fileDetails } = extractAssignmentFileDetailsFromMinioLocationOrKey( item )

                        return (
                            <Box display="flex" gap="20px" alignItems="center" key={item} padding="10px" color="GrayText" borderBottom={border[1]}>
                                <Typography onClick={() => viewSubmission( item )} variant='subtitle2'>{decodeURIComponent( fileDetails.fullFileName )}</Typography>
                                <IconButton onClick={() => viewSubmission( item )}>{Icons.small.VisibilityIcon}</IconButton>
                            </Box>
                        )
                    } )}
                </Box>
            </Popover>
            {previewFile && previewingFileDetails && <DrawerDocumentViewer loadingText='Loading submission' type={previewingFileDetails.fileExtension} fileName={decodeURIComponent( previewingFileDetails.fullFileName )} setPreviewingFileDetails={setPreviewingFileDetails} setPreviewFile={setPreviewFile} file={previewFile} />}
            {selectedValue && <Box>
                <Box sx={{
                    background: `linear-gradient(322.12deg, #F3C400 -21.29%, #E25A26 124.22%)`
                }} display="flex" alignItems="center" justifyContent="space-between" padding="20px" >
                    <Box>
                        <Typography color="white" variant="h6">{selectedValue.assignment_name} submissions</Typography>
                        <Typography color="white" variant="subtitle2">Due Date: {new Date( selectedValue.assignment_due_date ).toLocaleDateString( "en-IN", { day: "2-digit", month: "2-digit", year: "numeric" } ).replace( /\//g, "-" )}</Typography>
                    </Box>
                    <IconButton onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
                </Box>
            </Box>}
            <Box padding="20px">
                {submissions.length > 0 && <Typography fontSize="14px" gutterBottom><em><strong>{submissions.length}</strong> Submissions received</em></Typography>}
                {selectedValue && !loading && !error && submissions.length > 0 && <Box borderRadius="10px" overflow="hidden" bgcolor={palette.contentBg}>
                    <TableContainer sx={{ maxHeight: "450px", overflow: "auto" }} className='custom-scrollbar'>
                        <Table stickyHeader>
                            <TableHeader columns={columns} />
                            <TableBody>
                                {submissions.map( sub => (
                                    <TableRow key={sub.assignment_submission_id} className='table-row'>
                                        <TableCell sx={{ fontSize: "12px", padding: columns[0].padding || "10px 20px", color: table.color }} align={columns[0].align || "left"} > <strong>{sub.student_name}</strong> </TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", color: table.color }} align={columns[1].align || "left"} > <strong>{sub.assignment_submitted_by}</strong> </TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", color: table.color }} align={columns[2].align || "left"} > <strong>{formatDate( sub.assignment_submitted_on )}</strong> </TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }} align={columns[3].align || "left"} > <strong>{sub.assignment_marks || "Not assigned yet!"}</strong> </TableCell>
                                        <TableCell sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }} align={columns[4].align || "left"} >
                                            <Button onClick={e => {
                                                setSubmissionFiles( sub.assignment_submission_file_paths )
                                                setSelectedSubmissionAnchor( e.currentTarget )
                                            }} size="small" color="primary" sx={{ textTransform: "capitalize" }}  >View Submissions</Button>

                                        </TableCell>
                                    </TableRow>
                                ) )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>}
                {loading && !error && <DataLoadingSpinner waitingMessage="Please wait while we fetch assignment's submissions..." />}
                {error && <TryAgainMessageBlock err={error.err} code={error.code} getData={getData} loading={loading} />}
            </Box>
        </Dialog>
    )
}

export default SubmissionsViewer
